import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, Flag, MessageSquare, Share2, MapPin, Clock, Eye, BadgeCheck, Trash2, HandHeart, HelpingHand, MoreVertical, Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth, API } from '../../App';
import { useHubT } from './hubStrings';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Textarea } from '../ui/textarea';
import { ShareInfoDialog } from './ShareInfoDialog';
import { timeAgo } from './IncidentCard';

const CAT_STYLES = {
  smoke_seen: 'bg-orange-50 text-orange-800 ring-orange-200',
  fire_location: 'bg-red-50 text-red-800 ring-red-200',
  road_blocked: 'bg-amber-50 text-amber-800 ring-amber-200',
  power_outage: 'bg-slate-100 text-slate-800 ring-slate-300',
  flooding: 'bg-blue-50 text-blue-800 ring-blue-200',
  fallen_tree: 'bg-lime-50 text-lime-800 ring-lime-200',
  help_needed: 'bg-rose-50 text-rose-800 ring-rose-200',
  help_offered: 'bg-emerald-50 text-emerald-800 ring-emerald-200',
  animal_assistance: 'bg-teal-50 text-teal-800 ring-teal-200',
  general_update: 'bg-purple-50 text-purple-800 ring-purple-200',
};

const safeFormatDateTime = (iso, lang) => {
  try {
    return new Date(iso).toLocaleString(lang, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch {
    try {
      return new Date(iso).toLocaleString('en', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
    } catch { return iso ? iso.slice(0, 16).replace('T', ' ') : ''; }
  }
};

export const CommunityReportCard = ({ report, onChanged }) => {
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const hubT = useHubT();
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [disputeReason, setDisputeReason] = useState('false_information');
  const [disputeNote, setDisputeNote] = useState('');
  const [disputeBusy, setDisputeBusy] = useState(false);
  const [repliesOpen, setRepliesOpen] = useState(false);
  const [replies, setReplies] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replyBusy, setReplyBusy] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const goSignIn = () => navigate('/signin', { state: { from: `${location.pathname}${location.search || ''}` } });
  const authHeaders = user ? { Authorization: `Bearer ${user.token}` } : {};
  const isExpired = report.status === 'expired';
  const isResolved = report.status === 'resolved';
  const isHelp = report.category === 'help_needed' || report.category === 'help_offered';
  const isOfficialAuthor = ['town_hall', 'police', 'admin'].includes(report.authorRole);
  // v6.3 — scheduling. UPCOMING = event has a future start time.
  const nowIso = new Date().toISOString();
  const eventStart = report.eventStartAt || null;
  const eventEnd = report.eventEndAt || null;
  const isUpcoming = !isExpired && !isResolved && eventStart && eventStart > nowIso;

  const confirm = async () => {
    if (!user) { goSignIn(); return; }
    setConfirmBusy(true);
    try {
      const r = await axios.post(`${API}/community-reports/${report.id}/confirm`, {}, { headers: authHeaders });
      if (!r.data.alreadyConfirmed) toast.success(hubT('confirmed'));
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally {
      setConfirmBusy(false);
    }
  };

  const dispute = async () => {
    setDisputeBusy(true);
    try {
      await axios.post(`${API}/community-reports/${report.id}/dispute`,
        { reason: disputeReason, note: disputeNote }, { headers: authHeaders });
      toast.success(hubT('sent'));
      setDisputeOpen(false); setDisputeNote('');
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally {
      setDisputeBusy(false);
    }
  };

  const resolve = async () => {
    try {
      await axios.post(`${API}/community-reports/${report.id}/resolve`, {}, { headers: authHeaders });
      toast.success(hubT('resolved'));
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };

  const adminRemove = async () => {
    try {
      await axios.post(`${API}/admin/community-reports/${report.id}/remove`, {}, { headers: authHeaders });
      toast.success(hubT('removePost'));
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };

  const deleteOwn = async () => {
    setDeleteBusy(true);
    try {
      await axios.delete(`${API}/community-reports/${report.id}`, { headers: authHeaders });
      toast.success(hubT('deleteDone'));
      setDeleteOpen(false);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally {
      setDeleteBusy(false);
    }
  };

  const loadReplies = async () => {
    setRepliesOpen(o => !o);
    if (replies) return;
    try {
      const r = await axios.get(`${API}/community-reports/${report.id}/replies`);
      setReplies(r.data.replies || []);
    } catch { setReplies([]); }
  };

  const sendReply = async () => {
    if (!user) { goSignIn(); return; }
    setReplyBusy(true);
    try {
      const r = await axios.post(`${API}/community-reports/${report.id}/replies`,
        { body: replyText }, { headers: authHeaders });
      setReplies(prev => [...(prev || []), r.data.reply]);
      setReplyText('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally {
      setReplyBusy(false);
    }
  };

  const share = async () => {
    const origin = window.location.origin;
    const url = report.incidentId
      ? `${origin}/incidents/${report.incidentId}?report=${report.id}`
      : `${origin}/safety-bulletin?tab=community&report=${report.id}`;
    const text = `${hubT(`rc_${report.category}`)} — ${report.town}: ${report.body.slice(0, 100)}`;
    if (navigator.share) {
      try { await navigator.share({ title: 'Find-2-Day', text, url }); return; } catch { /* cancelled */ }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success(i18n.language === 'es' ? 'Enlace copiado' : 'Link copied');
    } catch { /* noop */ }
  };

  const badge = CAT_STYLES[report.category] || CAT_STYLES.general_update;
  const HelpIcon = report.category === 'help_needed' ? HandHeart : HelpingHand;
  const isAdmin = user?.role === 'admin';

  return (
    <article
      className={`bg-white rounded-xl border p-4 shadow-sm ${isExpired || isResolved ? 'opacity-60 border-gray-200' : 'border-gray-200'}`}
      data-testid={`community-report-${report.id}`}
    >
      <div className="flex items-start justify-between gap-2 flex-wrap mb-1.5">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ring-1 ${badge}`}>
            {isHelp && <HelpIcon className="h-3 w-3" />}
            {hubT(`rc_${report.category}`)}
          </span>
          {report.helpType && (
            <span className="text-[11px] text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full">{hubT(`ht_${report.helpType}`)}</span>
          )}
          {(isExpired || isResolved) && (
            <span className="text-[11px] font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
              {isResolved ? hubT('resolved') : hubT('expired')}
            </span>
          )}
          {isUpcoming && (
            <span
              className="text-[11px] font-bold uppercase tracking-wide text-amber-800 bg-amber-100 ring-1 ring-amber-300 px-2 py-0.5 rounded-full"
              data-testid={`report-upcoming-${report.id}`}
            >
              {hubT('upcoming')}
            </span>
          )}
        </div>
        <span className="text-[11px] text-gray-400">{timeAgo(report.createdAt, i18n.language)}</span>
      </div>

      <div className="flex items-center gap-2 flex-wrap text-[11px] text-gray-500 mb-2">
        <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{report.town}{report.area ? ` · ${report.area}` : ''}</span>
        {report.timeSeen && (
          <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />
            {safeFormatDateTime(report.timeSeen, i18n.language)}
          </span>
        )}
        {report.witnessed && (
          <span className="inline-flex items-center gap-1 text-emerald-700 font-medium"><Eye className="h-3 w-3" />{hubT('witnessed')}</span>
        )}
      </div>

      <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed mb-2">{report.body}</p>

      {(eventStart || eventEnd) && (
        <div
          className={`text-[11px] rounded-lg px-2.5 py-1.5 mb-2 ring-1 ${
            isUpcoming ? 'bg-amber-50 text-amber-900 ring-amber-200' : 'bg-slate-50 text-slate-700 ring-slate-200'
          }`}
          data-testid={`report-event-dates-${report.id}`}
        >
          {eventStart && (
            <div>
              <span className="font-semibold">{hubT('starts')}:</span>{' '}
              {safeFormatDateTime(eventStart, i18n.language)}
            </div>
          )}
          {eventEnd && (
            <div>
              <span className="font-semibold">{hubT('expectedToEnd')}:</span>{' '}
              {safeFormatDateTime(eventEnd, i18n.language)}
            </div>
          )}
        </div>
      )}

      {Array.isArray(report.photos) && report.photos.length > 0 && (
        <div
          className={`grid gap-1.5 mb-2 ${report.photos.length === 1 ? 'grid-cols-1' : report.photos.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}
          data-testid={`report-photos-${report.id}`}
        >
          {report.photos.slice(0, 3).map((url, idx) => (
            <a
              key={url}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="block aspect-square rounded-lg overflow-hidden border border-gray-200 bg-gray-50"
              data-testid={`report-photo-${report.id}-${idx}`}
            >
              <img src={url} alt="" loading="lazy" className="h-full w-full object-cover hover:opacity-95 transition-opacity" />
            </a>
          ))}
        </div>
      )}

      <div className="flex items-center gap-1 text-[11px] text-gray-500 mb-2">
        {isOfficialAuthor && <BadgeCheck className="h-3.5 w-3.5 text-blue-600" />}
        <span className={isOfficialAuthor ? 'font-semibold text-blue-700' : ''}>{report.authorDisplay}</span>
      </div>

      <div className="flex items-center gap-2 flex-wrap border-t border-gray-100 pt-2">
        <button
          onClick={confirm}
          disabled={confirmBusy || report.confirmedByMe || report.isMine}
          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors ${
            report.confirmedByMe
              ? 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-300'
              : 'bg-slate-50 text-slate-700 ring-1 ring-slate-200 hover:bg-emerald-50'
          } disabled:opacity-60`}
          data-testid={`report-confirm-${report.id}`}
        >
          <CheckCircle2 className="h-3.5 w-3.5" />
          {hubT('confirmSeen')}{report.confirmCount > 0 ? ` · ${report.confirmCount}` : ''}
        </button>
        <button
          onClick={loadReplies}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-slate-50 text-slate-700 ring-1 ring-slate-200 hover:bg-slate-100"
          data-testid={`report-replies-${report.id}`}
        >
          <MessageSquare className="h-3.5 w-3.5" />
          {hubT('replies')}{report.replyCount > 0 ? ` · ${report.replyCount}` : ''}
        </button>
        <button
          onClick={share}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-slate-50 text-slate-700 ring-1 ring-slate-200 hover:bg-slate-100"
          data-testid={`report-share-${report.id}`}
        >
          <Share2 className="h-3.5 w-3.5" />{hubT('share')}
        </button>
        <span className="ml-auto flex items-center gap-2">
          {report.isMine && !isResolved && !isExpired && (
            <button onClick={resolve} className="text-[11px] text-emerald-700 underline" data-testid={`report-resolve-${report.id}`}>
              {hubT('markResolved')}
            </button>
          )}
          {report.isMine && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  aria-label={hubT('ownerMenu')}
                  className="p-1 -mr-1 rounded-full text-gray-500 hover:text-gray-800 hover:bg-gray-100 inline-flex items-center"
                  data-testid={`report-owner-menu-${report.id}`}
                >
                  <MoreVertical className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40" data-testid={`report-owner-menu-list-${report.id}`}>
                <DropdownMenuItem onClick={() => setEditOpen(true)} data-testid={`report-edit-${report.id}`}>
                  <Pencil className="h-3.5 w-3.5 mr-2" />{hubT('editAlert')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setDeleteOpen(true)}
                  className="text-red-600 focus:text-red-600"
                  data-testid={`report-delete-${report.id}`}
                >
                  <Trash2 className="h-3.5 w-3.5 mr-2" />{hubT('deleteAlert')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {isAdmin && !report.isMine && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  aria-label={hubT('ownerMenu')}
                  className="p-1 -mr-1 rounded-full text-gray-500 hover:text-gray-800 hover:bg-gray-100 inline-flex items-center"
                  data-testid={`report-admin-menu-${report.id}`}
                >
                  <MoreVertical className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44" data-testid={`report-admin-menu-list-${report.id}`}>
                <DropdownMenuItem onClick={() => setEditOpen(true)} data-testid={`report-edit-${report.id}`}>
                  <Pencil className="h-3.5 w-3.5 mr-2" />{hubT('editAlert')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setDeleteOpen(true)}
                  className="text-red-600 focus:text-red-600"
                  data-testid={`report-delete-${report.id}`}
                >
                  <Trash2 className="h-3.5 w-3.5 mr-2" />{hubT('deleteAlert')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={adminRemove} data-testid={`report-admin-remove-${report.id}`}>
                  <Flag className="h-3.5 w-3.5 mr-2" />{hubT('removePost')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <button
            onClick={() => { if (!user) { goSignIn(); return; } setDisputeOpen(true); }}
            className="text-[11px] text-gray-400 hover:text-red-600 inline-flex items-center gap-0.5"
            data-testid={`report-dispute-${report.id}`}
          >
            <Flag className="h-3 w-3" />{hubT('reportIncorrect')}
          </button>
        </span>
      </div>

      {repliesOpen && (
        <div className="mt-3 border-t border-gray-100 pt-2 space-y-2" data-testid={`report-replies-panel-${report.id}`}>
          {(replies || []).map(r => (
            <div key={r.id} className="bg-gray-50 rounded-lg px-3 py-2">
              <p className="text-xs text-gray-800">{r.body}</p>
              <p className="text-[10px] text-gray-500 mt-0.5 flex items-center gap-1">
                {['town_hall', 'police', 'admin'].includes(r.authorRole) && <BadgeCheck className="h-3 w-3 text-blue-600" />}
                {r.authorDisplay} · {timeAgo(r.createdAt, i18n.language)}
              </p>
            </div>
          ))}
          <div className="flex gap-2">
            <input
              value={replyText}
              onChange={e => setReplyText(e.target.value)}
              placeholder={hubT('writeReply')}
              maxLength={300}
              className="flex-1 border rounded-lg px-3 py-1.5 text-sm"
              data-testid={`report-reply-input-${report.id}`}
            />
            <Button
              size="sm"
              onClick={sendReply}
              disabled={replyBusy || replyText.trim().length < 2}
              className="bg-slate-800 text-white"
              data-testid={`report-reply-send-${report.id}`}
            >
              {hubT('reply')}
            </Button>
          </div>
        </div>
      )}

      <Dialog open={deleteOpen} onOpenChange={(o) => { if (!o) setDeleteOpen(false); }}>
        <DialogContent data-testid="report-delete-dialog">
          <DialogHeader><DialogTitle>{hubT('deleteAlertTitle')}</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-600">{hubT('deleteAlertBody')}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)} disabled={deleteBusy} data-testid="report-delete-cancel">
              {hubT('cancel')}
            </Button>
            <Button onClick={deleteOwn} disabled={deleteBusy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="report-delete-confirm">
              {hubT('deleteAlert')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {editOpen && (
        <ShareInfoDialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          onShared={() => { onChanged?.(); }}
          isAuthority={['town_hall', 'police', 'admin'].includes(user?.role)}
          existingReport={report}
        />
      )}

      <Dialog open={disputeOpen} onOpenChange={(o) => { if (!o) setDisputeOpen(false); }}>
        <DialogContent data-testid="report-dispute-dialog">
          <DialogHeader><DialogTitle>{hubT('reportIncorrect')}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {['false_information', 'spam', 'abuse', 'other'].map(r => (
              <label key={r} className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  name={`dispute-${report.id}`}
                  checked={disputeReason === r}
                  onChange={() => setDisputeReason(r)}
                  data-testid={`dispute-reason-${r}`}
                />
                {r === 'false_information' ? hubT('falseInfo') : r === 'spam' ? hubT('spam') : r === 'abuse' ? hubT('abuse') : hubT('otherReason')}
              </label>
            ))}
            <Textarea
              value={disputeNote}
              onChange={e => setDisputeNote(e.target.value)}
              placeholder={hubT('whyIncorrect')}
              rows={3}
              maxLength={300}
              data-testid="dispute-note-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisputeOpen(false)}>{hubT('cancel')}</Button>
            <Button onClick={dispute} disabled={disputeBusy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="dispute-submit-btn">
              {hubT('submit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </article>
  );
};

export default CommunityReportCard;
